import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Store, Sprout, Wallet, User } from "lucide-react";
import { AuthContext } from "../../contexts/AuthContext";
import { ToastContext } from "../../contexts/ToastContext";
import { apiFetch } from "../../services/api";
import { useApi } from "../../hooks/useApi";
import LoadingState from "../../components/LoadingState";
import { T } from "../../constants/theme";

export default function SecondaryMarketPage() {
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const toast = useContext(ToastContext);
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [qtyByListing, setQtyByListing] = useState({});

  const { data, loading } = useApi("/market/listings", [reloadKey]);
  const listings = data?.data || [];

  const buy = async (listing) => {
    if (!user) {
      toast("Sign in to buy shares", "error");
      navigate("/login");
      return;
    }
    const qty = Number(qtyByListing[listing.id] || 1);
    if (!qty || qty <= 0) {
      toast("Enter a valid quantity", "error");
      return;
    }
    setBusy(true);
    try {
      const res = await apiFetch(
        `/market/listings/${listing.id}/buy`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ qty }),
        },
        { authToken: token },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || body.error || "Unable to complete trade");
      toast("Shares purchased");
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast(e.message || "Trade failed", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 20px 80px" }}>
      <style>{`
        .market-card { transition: transform .2s ease, border-color .2s ease, box-shadow .2s ease; }
        .market-card:hover { transform: translateY(-3px); border-color: rgba(95,203,140,0.35) !important; box-shadow: ${T.shadowSoft}; }
        .market-qty:focus { border-color: ${T.gold} !important; box-shadow: 0 0 0 3px ${T.goldLight}; }
        .market-buy:hover:not(:disabled) { transform: translateY(-1px); box-shadow: ${T.shadowMedium}; }
        .market-image img { transition: transform .6s ease; }
        .market-card:hover .market-image img { transform: scale(1.06); }
      `}</style>

      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 6 }}>
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 14,
            background: T.goldLight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Store size={24} color={T.gold} />
        </div>
        <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", color: T.ink, margin: 0 }}>
          Secondary Market
        </h1>
      </div>
      <p style={{ color: T.muted, marginBottom: 32 }}>
        Buy shares other investors are reselling. Want to sell shares you
        already hold?{" "}
        <button
          onClick={() => navigate("/portfolio")}
          style={{ border: "none", background: "none", color: T.gold, fontWeight: 700, cursor: "pointer" }}
        >
          Go to your portfolio.
        </button>
      </p>

      {loading ? (
        <LoadingState label="Loading listings..." />
      ) : listings.length === 0 ? (
        <div
          style={{
            color: T.muted,
            border: `1px dashed ${T.border}`,
            borderRadius: 16,
            padding: 40,
            textAlign: "center",
          }}
        >
          <Store size={28} color={T.muted} style={{ marginBottom: 10 }} />
          <div>No active listings right now.</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
          {listings.map((l) => {
            const pctAvailable = Math.round((l.qty_remaining / l.qty_total) * 100);
            return (
              <div
                key={l.id}
                className="market-card"
                style={{
                  background: T.surface,
                  border: `1px solid ${T.border}`,
                  borderRadius: 16,
                  padding: 20,
                  boxShadow: T.shadowSoft,
                }}
              >
                <div
                  className="market-image"
                  style={{
                    width: "100%",
                    aspectRatio: "3 / 4",
                    borderRadius: 14,
                    marginBottom: 14,
                    background: T.paper,
                    border: `1px solid ${T.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {l.product_image_url ? (
                    <img
                      src={l.product_image_url}
                      alt={l.product_name}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Sprout size={30} color={T.muted} />
                  )}
                </div>

                <div style={{ fontWeight: 700, color: T.ink, fontSize: 16 }}>{l.product_name}</div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 12,
                    color: T.muted,
                    marginTop: 4,
                    marginBottom: 12,
                  }}
                >
                  <User size={12} />
                  Sold by {l.seller_first_name || "an investor"}
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    marginBottom: 10,
                  }}
                >
                  <div style={{ fontSize: 20, color: T.gold, fontWeight: 800 }}>
                    LKR {Number(l.price_per_share).toFixed(2)}
                    <span style={{ fontSize: 12, color: T.muted, fontWeight: 500 }}> /share</span>
                  </div>
                  {l.product_price != null && (
                    <div style={{ fontSize: 11, color: T.muted }}>
                      list LKR {Number(l.product_price).toFixed(2)}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ height: 5, borderRadius: 3, background: T.paper, overflow: "hidden" }}>
                    <div
                      style={{
                        height: "100%",
                        width: `${pctAvailable}%`,
                        background: T.gold,
                      }}
                    />
                  </div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>
                    {l.qty_remaining} of {l.qty_total} shares available
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    className="market-qty"
                    type="number"
                    min={1}
                    max={l.qty_remaining}
                    placeholder="Qty"
                    value={qtyByListing[l.id] || ""}
                    onChange={(e) =>
                      setQtyByListing((m) => ({ ...m, [l.id]: e.target.value }))
                    }
                    style={{
                      width: 80,
                      padding: "12px",
                      borderRadius: 10,
                      border: `1px solid ${T.border}`,
                      background: T.paper,
                      color: T.ink,
                      outline: "none",
                    }}
                  />
                  <button
                    className="market-buy"
                    onClick={() => buy(l)}
                    disabled={busy}
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                      background: `linear-gradient(135deg, ${T.gold}, ${T.goldHover})`,
                      color: T.white,
                      border: "none",
                      borderRadius: 10,
                      fontWeight: 700,
                      cursor: "pointer",
                      transition: "transform .2s ease, box-shadow .2s ease",
                    }}
                  >
                    <Wallet size={15} />
                    Buy
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
